import { useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import useEbay from "../../data/useEbay";
import { IPart } from "../../models/Part";
import useOffers from "../../data/useOffers";
import LoadingWheel from "../shared/LoadingWheel";
import EbayListingTitle from "./EbayListingTitle";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import EditListingSpecifics from "./EditListingSpecifics";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useNotification from "../notifications/useNotifications";
import CustomJoditEditor from "../shared/Inputs/CustomJoditEditor";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import checkEbayDisabled, { ebayChecks } from "../../utils/checkEbayDisabled";
import { carryAutoAcceptPrice, resolveEffectiveAutoAcceptPrice } from "../../utils/bestOfferPricing";
import PriceFormat from "../../utils/priceFormat";
import { FormatType, IEbayOffer, ListingDuration } from "../../models/EbayOffer";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import useTemplateConditionDescription, { CONDITION_DESCRIPTION_MAX_LENGTH, DEFAULT_CONDITION_DESCRIPTION } from "../../data/useTemplateConditionDescription";
import EditListingCoreFields from "./EditListingCoreFields";


export type EbayListingFormValues = {
  title: string;
  description: string;
  format: FormatType;
  duration: ListingDuration;
  listingStarted: string | null;
  listNow: boolean;
  auctionStartPrice: number | null;
  auctionReservePrice: number | null;
  buyItNowPrice: number | null;
  enableBestOffer: boolean | null;
  autoAcceptPrice: number | null;
  locationKey: any;
  fulfillmentPolicyId: any;
  paymentPolicyId: any;
  returnPolicyId: any;
  categoryId: any;
  condition: any;
  conditionName?: string;
  conditionDescription: string;
};

// Form fields come back as strings once edited; compare prices to the penny.
const toPrice = (value: unknown) => Math.round((Number(value) || 0) * 100) / 100;

const defaultValues = {
  fulfillmentPolicyId: "125494812024",
  paymentPolicyId: "121933662024",
  returnPolicyId: "204693254024",
};

export default function EditListing({ part, page, setPage }: { part: IPart, page: number, setPage: React.Dispatch<React.SetStateAction<number>> }) {
  const { defaultPartCategory, useDefaultPartCategory } = usePartDefaultCategories({ part: part });
  const { createCustomAspects } = usePartAspectsMutations();
  const { create, update } = useOffers();
  const { policies, locations } = useEbay();
  const { withdraw, createOffer } = useSinglePartMutations(part.id);
  const { productDescription, updateProductDescription } = useTemplateProductDescription(part);
  // Only needed to fill an offer with no condition description yet - saved text is never replaced.
  const { conditionDescription } = useTemplateConditionDescription(part, !part.offers?.[0]?.conditionDescription);

  const { saveHandlers } = useDefaultCRUDHandlers("Offer");
  const { addNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [withdrawIsLoading, setWithdrawIsLoading] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [applyDefaultsOpen, setApplyDefaultsOpen] = useState(false);
  const [autoAcceptOverride, setAutoAcceptOverride] = useState<{ partOffer: any; nextPage: boolean } | null>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [description, setDescription] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loadError, setLoadError] = useState<string>("");
  // const [page, setPage] = useState<number>(0);
  const [isHovering, setIsHovering] = useState(false);
  // Set while a save is in flight or after one fails, so the optimistic update / rollback
  // changing `part` doesn't reset the form and wipe the user's unsaved edits.
  const preserveFormRef = useRef(false);

  // The API refuses a save with the marketplace's own reason - one channel refusing rolls the whole
  // save back. "Failed to save offer" on its own left the user nothing to act on.
  const showSaveError = (err: any) =>
    addNotification({
      variant: "error",
      primaryText: "Failed to save offer",
      secondaryText: err?.message,
    });

  const defaultVals = {
    title: `${part.vehicle?.dvlaYearOfManufacture} ${part.vehicle?.combinedMake} ${part.vehicle?.combinedModel} ${part.title}`,
    format: FormatType.FixedPrice,
    duration: ListingDuration.GTC,
    listingStarted: DateTime.now().plus({ minutes: 30 }).toFormat("yyyy-MM-dd'T'HH:mm"),
    auctionStartPrice: 0,
    auctionReservePrice: 0,
    buyItNowPrice: 0,
    enableBestOffer: false,
    autoAcceptPrice: 0,
    fulfillmentPolicyId: undefined,  //Policies are loaded in from the category defaults
    paymentPolicyId: undefined,
    returnPolicyId: undefined,
    categoryId: undefined, //SET TO UNDEFINED AS GRABS A VALUE IN THE CUSTOM SELECT
    storeCategoryId: undefined, //SET TO UNDEFINED AS GRABS A VALUE IN THE CUSTOM SELECT
    locationKey: undefined,
    condition: 3000,
    conditionDescription: conditionDescription.data?.data ?? DEFAULT_CONDITION_DESCRIPTION,
    listNow: true,
  };

  const { register, getValues, setValue, watch, handleSubmit, reset, formState, control } =
    useForm<EbayListingFormValues>({
      defaultValues: defaultVals
    });

  const onSubmit = (data: EbayListingFormValues, nextPage: boolean) => {
    // eBay refuses anything longer, so stop here rather than on the revise.
    if ((data.conditionDescription ?? "").length > CONDITION_DESCRIPTION_MAX_LENGTH) {
      addNotification({
        primaryText: `Condition description is too long - eBay allows ${CONDITION_DESCRIPTION_MAX_LENGTH.toLocaleString()} characters`,
        variant: "error",
      });
      return;
    }

    let partOffer = {
      ...part.offers[0],
      ...data,
      partDataId: part.id,
      quantity: 1,
      description: description,
      // condition: parseInt(data.condition),
    };

    if (partOffer.enableBestOffer) {
      const partCategoryDefault = defaultPartCategory.data?.data;

      if (!partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Buy It Now Price is required if enable best offer is enabled",
          variant: "error",
        });
        return;
      }

      // No explicit auto accept price is fine: the backend resolves one (explicit >
      // category threshold % > global default %) when publishing. We only block when the
      // resolved price would be higher than the Buy It Now price.
      const effectiveAutoAcceptPrice = resolveEffectiveAutoAcceptPrice(
        partOffer.buyItNowPrice,
        partOffer.autoAcceptPrice,
        partCategoryDefault?.autoAcceptThreshold,
      );

      if (effectiveAutoAcceptPrice && effectiveAutoAcceptPrice > partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Auto Accept Price cannot be higher than Buy It Now Price",
          variant: "error",
        });
        return;
      }
    } else {
      partOffer.autoAcceptPrice = null;
    }

    // Changing the price on its own moves the auto-accept with it (the API keeps the same % below Buy It
    // Now). An auto-accept typed in alongside the new price stops that, so check that's what they meant.
    const savedOffer = part.offers?.[0];
    if (savedOffer?.id && partOffer.enableBestOffer) {
      const newPrice = toPrice(partOffer.buyItNowPrice);
      const typedAutoAccept = toPrice(partOffer.autoAcceptPrice);

      if (newPrice !== toPrice(savedOffer.buyItNowPrice) && typedAutoAccept > 0 && typedAutoAccept !== toPrice(savedOffer.autoAcceptPrice)) {
        setAutoAcceptOverride({ partOffer, nextPage });
        return;
      }
    }

    saveOffer(partOffer, nextPage);
  };

  const saveOffer = (partOffer: any, nextPage: boolean) => {
    preserveFormRef.current = true;

    if (!partOffer.id) {
      create.mutate(partOffer, {
        onSuccess: () => {
          preserveFormRef.current = false;
          if (nextPage) setPage(1);
          saveHandlers.onSuccess();
        },
        onError: showSaveError,
      });
    } else {
      update.mutate(partOffer, {
        onSuccess: () => {
          preserveFormRef.current = false;
          setAutoAcceptOverride(null);
          if (nextPage) setPage(1);
          saveHandlers.onSuccess();
        },
        onError: (err: any) => {
          setAutoAcceptOverride(null);
          showSaveError(err);
        },
      });
    }
  };

  // Sends the auto-accept back as it was saved, so the API recalculates it from the new price.
  const handleRecalculateAutoAccept = () => {
    if (!autoAcceptOverride) return;

    const savedAutoAccept = part.offers?.[0]?.autoAcceptPrice ?? null;
    setValue("autoAcceptPrice", savedAutoAccept);
    saveOffer({ ...autoAcceptOverride.partOffer, autoAcceptPrice: savedAutoAccept }, autoAcceptOverride.nextPage);
  };

  const autoAcceptOverrideMessage = useMemo(() => {
    if (!autoAcceptOverride) return "";

    const { partOffer } = autoAcceptOverride;
    const savedOffer = part.offers?.[0];
    const newPrice = toPrice(partOffer.buyItNowPrice);
    const typed = PriceFormat(toPrice(partOffer.autoAcceptPrice));
    const carried = carryAutoAcceptPrice(savedOffer?.buyItNowPrice, savedOffer?.autoAcceptPrice, newPrice);

    const recalculation = carried
      ? `recalculating it to ${PriceFormat(carried.price)} (${carried.percent}% below the new price)`
      : "recalculating it from the new price";

    return `You've changed the Buy It Now price to ${PriceFormat(newPrice)} and typed in an auto-accept price of ${typed}.\n\nSaving will use ${typed} instead of ${recalculation}.`;
  }, [autoAcceptOverride, part.offers]);

  const handleSetFormat = (_: string, value: any) => {
    setValue(
      "duration",
      value === FormatType.FixedPrice
        ? ListingDuration.GTC
        : ListingDuration.Days30
    );
    setValue("format", value);
  };

  const handleIsDisabled = () => {
    const offerData: EbayListingFormValues = getValues();

    // If "List Now" is checked, ignore listing time validation
    const ignoreListingTime = offerData.listNow;

    const offer = offerData as IEbayOffer;

    const partCategoryDefault = defaultPartCategory.data?.data;

    let result: ebayChecks = checkEbayDisabled({ part, offer, partCategoryDefault, ignoreListingTime });

    setError(result.error);
    // setIsDisabled(result.isDisabled);

    if (!part.offers || part.offers.length === 0) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  };

  const handleWithdraw = () => {
    if (!part.offers[0].publishedOn)
      return;

    withdraw.mutate(part.offers[0].id, {
      onSuccess: () => {
        setWithdrawOpen(false);
        saveHandlers.onSuccess();
      },
      onError: showSaveError,
    });
  };

  const handleApplyDefaults = () => {
    setIsLoading(true);
    preserveFormRef.current = false;

    useDefaultPartCategory.mutate(undefined, {
      onSuccess: () => {
        setApplyDefaultsOpen(false);
        saveHandlers.onSuccess();
      },
      onError: showSaveError,
      onSettled: () => {
        setIsLoading(false);
      }

    });
  };

  const handleSetFirstLocation = () => {
    const data = getValues();

    const offer = part.offers?.[0];

    if (!data.locationKey && locations.data && locations.data.data.locations.length > 0) {
      if (offer && offer.locationKey) return;

      const firstWithName = locations.data.data.locations.filter(l => l.name && l.name.trim() !== "")?.[0];

      setValue("locationKey", firstWithName?.id);
    }
  }

  // isInitialLoading, not isLoading: a part with no category disables the defaults query, and in
  // react-query v4 a disabled query with no data reports isLoading forever - the tab never left
  // the spinner for uncategorised parts.
  useEffect(() => {
    if (!policies.isLoading && policies.data && !locations.isLoading && locations.data && !defaultPartCategory.isInitialLoading && !productDescription.isInitialLoading && !conditionDescription.isInitialLoading) {
      // console.log("Policies and Locations loaded");
      if (isLoading) {
        //Add default custom aspects
        createCustomAspects.mutate(part.id);
      }

      if (part.offers.length === 0) {

        // console.log("New Offer is Being Created!");
        // The spinner only clears when the part refetches with an offer, so a failure here has
        // to surface or the tab spins forever. The endpoint returns 200 with success=false.
        createOffer.mutate(part.id, {
          onSuccess: (res: any) => {
            if (res?.success === false) setLoadError(res.message ?? "Failed to create the listing offer.");
          },
          onError: (err: any) => setLoadError(err?.message ?? "Failed to create the listing offer."),
        }); // API call
        handleSetFirstLocation();

      } else if (part.offers?.length > 0) {
        // console.log(part.offers[0])

        //Map default values onto offer where offer value is null
        let offer = Object.fromEntries(
          Object.entries(part.offers[0]).map(([key, value]) => [
            key,
            value !== null ? value : Object.entries(defaultVals).filter(x => x[0] == key)[0]?.[1] ?? null,
          ])
        );


        if (!preserveFormRef.current) {
          reset(offer);
          //Set description
          setDescription(part.offers[0]?.description ?? productDescription.data?.data)
        }
        handleSetFirstLocation();
        setIsLoading(false);

      } else {
        //Generate description
        handleSetFirstLocation();
        setDescription(productDescription.data?.data ?? "");
        setIsLoading(false);
      }

      // setIsLoading(false);
      handleIsDisabled();
    }
  }, [part, policies.data, policies.isLoading, locations.data, locations.isLoading, defaultPartCategory.isInitialLoading, productDescription.isInitialLoading, conditionDescription.isInitialLoading]);


  const handleSetDescription = async () => {
    // console.log("Setting Description from Template", productDescription?.data);
    preserveFormRef.current = false;
    await updateProductDescription.mutateAsync(part.id)
  }

  //Check if form is valid
  useEffect(() => {
    handleIsDisabled();
    // console.log(getValues())
  }, [formState]);

  //Scroll to top on page load - this is mostly for when you go back from the second page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const bestOfferValue = watch("enableBestOffer");

  const isPublished = useMemo(() => {
    return part.offers && part.offers.length > 0 && part.offers[0].publishedOn !== null;
  }, [part.offers]);

  const blockingError =
    loadError ||
    (policies.isError ? `Failed to load eBay policies: ${(policies.error as Error)?.message}` : "") ||
    (locations.isError ? `Failed to load eBay locations: ${(locations.error as Error)?.message}` : "");

  if (isLoading && blockingError) {
    return (
      <div className="mt-[20vh] text-center text-red-600">
        {blockingError}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mt-[20vh]">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <ConfirmModal open={withdrawOpen} setOpen={setWithdrawOpen} isLoading={withdrawIsLoading}
        title={"Withdraw Listing?"}
        message={`Are you sure you want to withdraw this listing? \nThis will remove it from eBay and cannot be undone.`}
        confirmButtonText={"Withdraw Listing"}
        onConfirm={() => handleWithdraw()}
      />
      <ConfirmModal open={applyDefaultsOpen} setOpen={setApplyDefaultsOpen} isLoading={false}
        title={"Apply Defaults to Listing?"}
        message={`Are you sure you want to apply defaults to this listing? \nThis will change all the current information displayed.`}
        confirmButtonText={"Apply Defaults"}
        onConfirm={() => handleApplyDefaults()}
      />
      <ConfirmModal open={autoAcceptOverride !== null} setOpen={() => setAutoAcceptOverride(null)} isLoading={update.isLoading}
        title={"Use your auto-accept price?"}
        message={autoAcceptOverrideMessage}
        confirmButtonText={`Use ${PriceFormat(toPrice(autoAcceptOverride?.partOffer.autoAcceptPrice))}`}
        onConfirm={() => autoAcceptOverride && saveOffer(autoAcceptOverride.partOffer, autoAcceptOverride.nextPage)}
      >
        <button
          type="button"
          onClick={handleRecalculateAutoAccept}
          disabled={update.isLoading}
          className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          Recalculate it from the new price instead
        </button>
      </ConfirmModal>
      <div className="space-y-4">

        {/* Move from original form to specifics */}
        {page === 0 ? (
          <form onSubmit={handleSubmit(() => onSubmit(getValues(), false))} >
            <div className="grid grid-cols-1 pb-20 sm:grid-cols-2 gap-4">

              <EbayListingTitle part={part} register={register} getValues={getValues} watch={watch} setValue={setValue} />

              <div className="sm:col-span-2 -mt-10 flex justify-end items-center">
                <ButtonInput label="Reset Description" onClick={handleSetDescription} classes={undefined} isLoading={updateProductDescription.isLoading} />
              </div>

              <div className="sm:col-span-2">
                <CustomJoditEditor
                  content={description}
                  setContent={setDescription}
                  placeholder="Create your listing description..."
                  height={300}
                />
              </div>

              <EditListingCoreFields
                part={part}
                register={register}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                handleSetFormat={handleSetFormat}
                defaultPartCategory={defaultPartCategory}
                policies={policies}
                locations={locations}
                bestOfferValue={!!bestOfferValue}
                isPublished={isPublished}
              />

              <div className="sm:col-span-2 mt-4 flex justify-between">
                <div className="flex items-start gap-x-4">
                  {/* Withdraw button shows if listing has been published and not sold */}
                  {part.offers.length > 0 && part.offers[0].publishedOn !== null && !part.soldDate && (
                    <ButtonInput
                      label={"Withdraw Listing"}
                      isSubmit={false}
                      onClick={() => setWithdrawOpen(true)}
                      classes={"!bg-red-500 hover:!bg-red-600"}
                    />
                  )}
                  {part.offers.length > 0 && !part.soldDate && (
                    <div className="flex flex-col">
                      <ButtonInput
                        label={"Apply Defaults"}
                        isSubmit={false}
                        onClick={() => setApplyDefaultsOpen(true)}
                        classes={""}
                        disabled={!defaultPartCategory.data?.data}
                      />
                      {
                        !defaultPartCategory.data?.data &&
                        <span className="text-gray-600 text-sm">No defaults found</span>
                      }
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-4">
                  <ButtonInput
                    label={"Save"}
                    isSubmit={true}
                    onClick={undefined}
                    classes={undefined}
                    disabled={part.soldDate !== null}
                    isLoading={create.isLoading || update.isLoading}
                  />
                  <div
                    onMouseOver={() => setIsHovering(true)}
                    onMouseOut={() => setIsHovering(false)}
                    className="relative"
                  >
                    {isHovering && error && (
                      <div className="absolute bottom-10 w-[250px] bg-white border-2 border-gray-200">
                        <p className=" text-red-700 p-2">
                          {error}
                        </p>
                      </div>
                    )}
                    <ButtonInput
                      label={"Save & Next"}
                      isSubmit={false}
                      onClick={() => onSubmit(getValues(), true)}
                      classes={"!bg-white !border-1 !border-gray-400 !text-gray-600 hover:!bg-gray-200"}
                      disabled={isDisabled}
                      isLoading={create.isLoading || update.isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>

        ) : (
          <EditListingSpecifics part={part} setPage={setPage} />
        )}

      </div>
    </>
  );
}
